import {
    ActiveEnums,
    InsertAssignmentRosterStaffPostModel,
    UpdateRosterStaffActivationPostModel,
    UpdateRosterStaffProjectRolePostModel,
} from "@app/data/engine.ts";
import useAssignmentData from "@app/data-hooks/use-assignment-data.ts";
import useLookupListData from "@app/data-hooks/use-lookup-list-data.ts";
import useRosterData from "@app/data-hooks/use-roster-data.ts";
import useStaffData from "@app/data-hooks/use-staff-data.ts";
import { Button, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { Dialog, QueryStatusRouter, Select, SelectOption } from "bi-lib";
import { useMemo } from "react";

interface Props {
    assignmentId: number;
    open: boolean;
    onClose: () => void;
    rosterId?: number;
}

export default function AssignmentRosterModal({ open, onClose, assignmentId, rosterId }: Props) {
    const { insertRosterStaff, updateRosterStaffProjectRole, updateRosterStaffActivation } = useRosterData();
    const { staffingList } = useStaffData({
        activeState: rosterId == undefined ? ActiveEnums.ActiveOnly : ActiveEnums.All,
    });
    const { rosterList } = useAssignmentData({
        id: assignmentId,
        activeState: rosterId == undefined ? ActiveEnums.All : ActiveEnums.ActiveOnly,
    });
    const { projectRoles } = useLookupListData();

    const roster = useMemo(() => rosterList.data?.find((x) => x.rosterId == rosterId), [rosterList.data, rosterId]);

    const form = useForm({
        defaultValues: {
            staffId: roster?.staffId,
            projectRoleId: roster?.projectRoleId,
            isActive: roster?.active ?? false,
        } as Omit<
            InsertAssignmentRosterStaffPostModel &
                UpdateRosterStaffProjectRolePostModel &
                UpdateRosterStaffActivationPostModel,
            "assignmentId" | "rosterId"
        >,
        onSubmit: async ({ value }) => {
            // Create
            if (rosterId == undefined) {
                await insertRosterStaff.mutateAsync({
                    assignmentId,
                    staffId: value.staffId,
                    projectRoleId: value.projectRoleId,
                });
                handleClose();
                return;
            }

            // Update
            if (value.projectRoleId != roster?.projectRoleId) {
                await updateRosterStaffProjectRole.mutateAsync({
                    rosterId: rosterId,
                    projectRoleId: value.projectRoleId,
                });
            }

            if (value.isActive != roster?.active) {
                await updateRosterStaffActivation.mutateAsync({
                    rosterId: rosterId,
                    isActive: value.isActive,
                });
            }
            handleClose();
        },
    });

    const handleClose = () => {
        form.reset();
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth={"sm"} fullWidth>
            <DialogTitle>{rosterId != undefined ? "Update" : "Create"} Roster Entry</DialogTitle>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
            >
                <DialogContent>
                    <Stack spacing={1}>
                        <QueryStatusRouter queryHook={staffingList}>
                            <form.Field name={"staffId"}>
                                {(field) => (
                                    <Select
                                        label={"Staff"}
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e)}
                                        required
                                        options={staffingList.data?.reduce<SelectOption<number>[]>((acc, x) => {
                                            if (
                                                rosterId == undefined &&
                                                rosterList.data?.some((y) => y.staffId == x.staffingId)
                                            ) {
                                                return acc;
                                            }
                                            return [
                                                ...acc,
                                                {
                                                    label: `${x.lastName}, ${x.firstName}`,
                                                    value: x.staffingId,
                                                } as SelectOption<number>,
                                            ];
                                        }, [])}
                                        disabled={rosterId != undefined}
                                    />
                                )}
                            </form.Field>
                        </QueryStatusRouter>
                        <QueryStatusRouter queryHook={projectRoles}>
                            <form.Field name={"projectRoleId"}>
                                {(field) => (
                                    <Select
                                        label={"Role"}
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e)}
                                        options={projectRoles.data?.map((x) => ({
                                            label: x.roleDescription,
                                            value: x.projectRoleId,
                                        }))}
                                        required
                                    />
                                )}
                            </form.Field>
                        </QueryStatusRouter>
                        {rosterId != undefined && (
                            <QueryStatusRouter queryHook={rosterList}>
                                <form.Field name={"isActive"}>
                                    {(field) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={field.state.value}
                                                    onChange={(_, checked) => field.handleChange(checked)}
                                                />
                                            }
                                            label={"Active"}
                                        />
                                    )}
                                </form.Field>
                            </QueryStatusRouter>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant={"outlined"}>
                        Close
                    </Button>
                    <Button type={"submit"} variant={"contained"}>
                        Save
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
